import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../ui/confirm';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from '../../lib/toast';
import {
  SettingsSection,
  SettingsRow,
  SettingsHeader,
  StatusBadge,
  DangerSection,
  DangerRow
} from './SettingsUI';

const HomeKitSettings = () => {
  const { apiRequest } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [setupRes, statusRes] = await Promise.all([
        apiRequest('/api/homekit/setup'),
        apiRequest('/api/homekit/status')
      ]);
      setSetupData(setupRes.data);
      setStatusData(statusRes.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load HomeKit data');
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReset = async () => {
    const ok = await confirm({
      title: 'Reset HomeKit Pairing',
      description: 'You will need to re-add this hub to Apple Home.',
      confirmText: 'Reset',
      destructive: true,
    });
    if (!ok) {
      return;
    }

    setResetting(true);
    try {
      await apiRequest('/api/homekit/reset', { method: 'POST' });
      toast.success('HomeKit pairing reset successfully');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to reset HomeKit pairing');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-4">
        <SettingsHeader title="Apple HomeKit" />
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <SettingsHeader 
        title="Apple HomeKit" 
        description="Connect your hub to Apple Home"
      />

      {error && (
        <div className="px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {setupData?.setupUri && (
        <div className="px-4">
          <div className="p-4 bg-card rounded-xl border border-border/60 flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-lg">
              <QRCodeDisplay value={setupData.setupUri} />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Scan with your iPhone to add to Apple Home
            </p>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Manual Code</p>
              <p className="font-mono text-lg font-semibold">{setupData.pincode}</p>
            </div>
          </div>
        </div>
      )}

      <SettingsSection title="Hub Information">
        <SettingsRow 
          label="Bridge Name" 
          value={statusData?.bridgeName || 'Mirfa Smart Hub'} 
        />
        <SettingsRow 
          label="Status" 
          accessory={<StatusBadge status={statusData?.running ? 'running' : 'stopped'} />}
        />
        <SettingsRow 
          label="Pairing" 
          accessory={<StatusBadge status={statusData?.paired ? 'paired' : 'not paired'} />}
        />
        <SettingsRow 
          label="HAP Port" 
          value={statusData?.port || 51826} 
        />
        {statusData?.paired && (
          <>
            <SettingsRow label="Controllers" value={statusData?.controllersCount || 0} />
            <SettingsRow label="Accessories" value={statusData?.accessoryCount || 0} />
          </>
        )}
      </SettingsSection>

      <SettingsSection title="Network Requirements">
        <div className="px-4 py-3 text-sm text-muted-foreground space-y-1">
          <p>For HomeKit pairing to work:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>iPhone must be on the same network</li>
            <li>mDNS traffic allowed (UDP 5353)</li>
            <li>HAP port accessible (TCP {statusData?.port || 51826})</li>
          </ul>
        </div>
      </SettingsSection>

      <DangerSection>
        <DangerRow
          label="Reset Pairing"
          description="Remove this hub from Apple Home"
          buttonLabel={resetting ? 'Resetting...' : 'Reset'}
          onClick={handleReset}
          loading={resetting}
        />
      </DangerSection>
    </div>
  );
};

const QRCodeDisplay = ({ value }) => {
  const [QRCode, setQRCode] = useState(null);

  useEffect(() => {
    import('qrcode.react').then(module => {
      setQRCode(() => module.QRCodeSVG);
    }).catch(() => {
      setQRCode(null);
    });
  }, []);

  if (!QRCode) {
    return (
      <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
        <span className="text-xs text-muted-foreground text-center px-4">
          QR Code library not available
        </span>
      </div>
    );
  }

  return <QRCode value={value} size={192} level="M" />;
};

export default HomeKitSettings;
